import { useEffect, useState } from "react";

import type { AnnotationTarget } from "./types";

export interface TargetPresentation {
  readonly selectedOnly: boolean;
  readonly visibleTargetIds: ReadonlySet<string>;
  readonly targetNames: ReadonlyMap<string, string>;
}

interface TargetPresentationState extends TargetPresentation {
  readonly collectionId: string;
  readonly knownTargetIds: ReadonlySet<string>;
}

function createTargetPresentationState(
  collectionId: string,
  targets: readonly AnnotationTarget[],
  selectedTargetId: string | null,
): TargetPresentationState {
  const targetIds = new Set(targets.map((target) => target.id));
  const visibleTargetIds = new Set<string>();
  if (selectedTargetId !== null && targetIds.has(selectedTargetId)) {
    visibleTargetIds.add(selectedTargetId);
  }
  return {
    collectionId,
    knownTargetIds: targetIds,
    selectedOnly: true,
    visibleTargetIds,
    targetNames: new Map(),
  };
}

export function useTargetPresentation({
  collectionId,
  targets,
  selectedTargetId,
  onSelectTarget,
}: {
  collectionId: string;
  targets: readonly AnnotationTarget[];
  selectedTargetId: string | null;
  onSelectTarget: (targetId: string) => void;
}) {
  const [state, setState] = useState<TargetPresentationState>(() =>
    createTargetPresentationState(collectionId, targets, selectedTargetId),
  );
  const presentation =
    state.collectionId === collectionId
      ? state
      : createTargetPresentationState(collectionId, targets, selectedTargetId);

  useEffect(() => {
    const availableTargetIds = new Set(targets.map((target) => target.id));
    setState((current) => {
      if (current.collectionId !== collectionId) {
        return createTargetPresentationState(collectionId, targets, selectedTargetId);
      }

      const nextVisibleTargetIds =
        current.selectedOnly &&
        selectedTargetId !== null &&
        availableTargetIds.has(selectedTargetId)
          ? new Set([selectedTargetId])
          : new Set(
              [...current.visibleTargetIds].filter((targetId) => availableTargetIds.has(targetId)),
            );
      if (!current.selectedOnly) {
        for (const targetId of availableTargetIds) {
          if (!current.knownTargetIds.has(targetId)) nextVisibleTargetIds.add(targetId);
        }
      }
      const nextTargetNames = new Map(
        [...current.targetNames].filter(([targetId]) => availableTargetIds.has(targetId)),
      );
      const knownTargetsUnchanged =
        availableTargetIds.size === current.knownTargetIds.size &&
        [...availableTargetIds].every((targetId) => current.knownTargetIds.has(targetId));
      const visibilityUnchanged =
        nextVisibleTargetIds.size === current.visibleTargetIds.size &&
        [...nextVisibleTargetIds].every((targetId) => current.visibleTargetIds.has(targetId));
      const namesUnchanged = nextTargetNames.size === current.targetNames.size;
      if (knownTargetsUnchanged && visibilityUnchanged && namesUnchanged) return current;

      return {
        collectionId: current.collectionId,
        knownTargetIds: availableTargetIds,
        selectedOnly: current.selectedOnly,
        visibleTargetIds: nextVisibleTargetIds,
        targetNames: nextTargetNames,
      };
    });
  }, [collectionId, selectedTargetId, targets]);

  const resolveCurrent = (current: TargetPresentationState) =>
    current.collectionId === collectionId
      ? current
      : createTargetPresentationState(collectionId, targets, selectedTargetId);

  const setSelectedOnly = (selectedOnly: boolean) => {
    setState((current) => {
      const currentPresentation = resolveCurrent(current);
      return {
        ...currentPresentation,
        selectedOnly,
        visibleTargetIds:
          selectedOnly &&
          selectedTargetId !== null &&
          targets.some((target) => target.id === selectedTargetId)
            ? new Set([selectedTargetId])
            : currentPresentation.visibleTargetIds,
      };
    });
  };

  const setVisibleTargets = (targetIds: readonly string[]) => {
    const availableTargetIds = new Set(targets.map((target) => target.id));
    setState((current) => ({
      ...resolveCurrent(current),
      visibleTargetIds: new Set(targetIds.filter((targetId) => availableTargetIds.has(targetId))),
    }));
  };

  const setTargetVisibility = (targetId: string, visible: boolean) => {
    setState((current) => {
      const currentPresentation = resolveCurrent(current);
      if (currentPresentation.visibleTargetIds.has(targetId) === visible) {
        return currentPresentation;
      }
      const next = new Set(currentPresentation.visibleTargetIds);
      if (visible) next.add(targetId);
      else next.delete(targetId);
      return { ...currentPresentation, visibleTargetIds: next };
    });
  };

  const setTargetName = (targetId: string, name: string) => {
    setState((current) => {
      const currentPresentation = resolveCurrent(current);
      if (currentPresentation.targetNames.get(targetId) === name) return currentPresentation;
      const next = new Map(currentPresentation.targetNames);
      next.set(targetId, name);
      return { ...currentPresentation, targetNames: next };
    });
  };

  const selectTarget = (targetId: string) => {
    onSelectTarget(targetId);
    setState((current) => {
      const currentPresentation = resolveCurrent(current);
      return currentPresentation.selectedOnly
        ? { ...currentPresentation, visibleTargetIds: new Set([targetId]) }
        : currentPresentation;
    });
  };

  return {
    presentation,
    setSelectedOnly,
    setVisibleTargets,
    setTargetVisibility,
    setTargetName,
    selectTarget,
  };
}
